import type { ReactNode } from 'react'
import { useDouyinLiveAnalysis } from '../platform'
import { DOUYIN_SHUIJU_STORE_ID, useDouyinStoreId } from '../douyinStore'
import type { DouyinLiveAnalysis } from '../douyinTypes'
import { money } from '../utils'

export const DY_STORE_ID = DOUYIN_SHUIJU_STORE_ID

/** 金额：null/undefined 显示 —，其余 ¥两位小数。 */
export const dyYuan = (value: number | null | undefined) => value == null ? '—' : money(value)

/** 金额（0位小数，场次页用）。 */
export const dyYuan0 = (value: number | null | undefined) =>
  value == null ? '—' : `¥${Number(value).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`

/** 比率（0~1）→ 一位小数百分比。 */
export const dyPct1 = (value: number | null | undefined) => value == null ? '—' : `${(value * 100).toFixed(1)}%`

/** 比率（0~1）→ 两位小数百分比（漏斗/渠道用）。 */
export const dyPct2 = (value: number | null | undefined) => value == null ? '—' : `${(value * 100).toFixed(2)}%`

export const dyInt = (value: number | null | undefined) => value == null ? '—' : Number(value).toLocaleString('zh-CN')

/** 直播时长（分钟）→「x小时y分钟」；不足 1 小时只显示分钟，整小时不带分钟。 */
export const dyDuration = (minutes: number | null | undefined) => {
  if (minutes == null) return '—'
  const total = Math.round(minutes)
  const hours = Math.floor(total / 60)
  const rest = total % 60
  if (hours === 0) return `${rest}分钟`
  if (rest === 0) return `${hours}小时`
  return `${hours}小时${rest}分钟`
}

/** 金额 ≥1万 显示 x.x万（趋势纵轴用）。 */
export const dyWan = (value: number, isMoney: boolean) => {
  if (isMoney) return Math.abs(value) >= 10000 ? `¥${(value / 10000).toFixed(1)}万` : money(value)
  return Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : Math.round(value).toLocaleString('zh-CN')
}

/** 正负着色金额。 */
export function DySigned({ value }: { value: number | null | undefined }) {
  if (value == null) return <>—</>
  return <span className={value >= 0 ? 'profit-positive' : 'profit-negative'}>{money(value)}</span>
}

export type DyTagKind = 'nocost' | 'review' | 'off' | 'coded' | 'uncoded' | 'norm' | 'partner' | 'big' | 'warn'

export function DyTag({ kind, children }: { kind: DyTagKind; children: ReactNode }) {
  return <span className={`dy-tag ${kind}`}>{children}</span>
}

export function DouyinProductImage({ src, alt = '' }: { src: string | null; alt?: string }) {
  return src ? <img src={src} alt={alt} loading="lazy" /> : <span className="image-fallback">无图</span>
}

export function DouyinBoundary({ children }: { children: (data: DouyinLiveAnalysis) => ReactNode }) {
  const storeId = useDouyinStoreId()
  const resource = useDouyinLiveAnalysis(storeId)
  if (resource.loading) return <div className="loading-state">正在加载抖音直播经营数据…</div>
  if (resource.error || !resource.data) {
    return <div className="error-state">
      <h1>抖音直播经营数据不可用</h1>
      <p>{resource.error || '数据为空'}</p>
      <button type="button" className="primary-button" onClick={() => void resource.reload()}>重试</button>
    </div>
  }
  return <>{children(resource.data)}</>
}

/** 表头排序单元格。 */
export function DySortTh({
  label,
  columnKey,
  left,
  className = '',
  sort,
  onSort,
}: {
  label: string
  columnKey: string
  left?: boolean
  className?: string
  sort: { key: string; desc: boolean }
  onSort: (key: string) => void
}) {
  const active = sort.key === columnKey
  return <th
    className={`is-sortable ${left ? 'l' : ''} ${className}`.trim()}
    aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : undefined}
    onClick={() => onSort(columnKey)}
  >{label}{active ? <span className="dy-sort-arrow">{sort.desc ? '↓' : '↑'}</span> : null}</th>
}
